/* eslint-disable unicorn/prevent-abbreviations */
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { type AxiosError } from "axios";

import {
  ESignWorkflowSessionStatus,
  NON_SIGNABLE_ESIGN_WORKFLOW_SESSION_STATUSES,
} from "./constants";
import {
  type ESignWorkflowSessionListResponse,
  eSignWorkflowSessionListResponseSchema,
} from "./types";
import { useCreateESignWorkflowSession } from "./useCreateESignWorkflowSession";

const POLL_TIMEOUT_MS = 30_000;
const POLL_INTERVAL_MS = 1000;

interface SignableESignWorkflowSessionMutationVariables {
  compositeParentRequirementId?: string;
}

type ESignWorkflowSessionData = ESignWorkflowSessionListResponse["data"][number];

interface UseSignableESignWorkflowSessionProps extends UseMutationOptions<
  ESignWorkflowSessionData,
  AxiosError,
  SignableESignWorkflowSessionMutationVariables
> {
  requirementId: string;
}

function getESignWorkflowSessionsUrl(requirementId: string) {
  return `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/requirements/${requirementId}/esign-workflow-sessions`;
}

export async function fetchESignWorkflowSessions(params: {
  requirementId: string;
  workerId: string;
  filter?: {
    statuses: {
      ne: ESignWorkflowSessionStatus[];
    };
  };
}): Promise<ESignWorkflowSessionListResponse> {
  const { requirementId, workerId, filter } = params;

  const response = await get({
    url: getESignWorkflowSessionsUrl(requirementId),
    queryParams: {
      filter: {
        workerId: { eq: workerId },
        statuses: {
          ne: NON_SIGNABLE_ESIGN_WORKFLOW_SESSION_STATUSES,
        },
        ...(filter ?? {}),
      },
      include: "documents",
      sort: "-createdAt",
      page: { size: 1 },
    },
    responseSchema: eSignWorkflowSessionListResponseSchema,
  });

  return response.data;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function pollForPandaDocSessionId(params: {
  requirementId: string;
  workerId: string;
}): Promise<ESignWorkflowSessionListResponse["data"][number]> {
  const startTime = Date.now();

  while (Date.now() - startTime < POLL_TIMEOUT_MS) {
    await sleep(POLL_INTERVAL_MS);

    const response = await fetchESignWorkflowSessions(params);
    const [session] = response.data;
    const pandaDocSessionId = session?.attributes.esignProviderData?.sessionId;

    if (isDefined(pandaDocSessionId)) {
      return session;
    }
  }

  throw new Error("Timed out waiting for PandaDoc session ID");
}

/**
 * Returns a mutation that resolves with an ESignWorkflowSession
 * that has a pandaDocSessionId ready for embedded signing.
 *
 * Creates a new session if none exists, then polls until the backend
 * populates the pandaDocSessionId (up to 30 seconds).
 */
export function useSignableESignWorkflowSession(props: UseSignableESignWorkflowSessionProps) {
  const { requirementId, ...restOptions } = props;
  const worker = useDefinedWorker();
  const workerId = worker.userId;
  const { mutateAsync: createESignWorkflowSession } = useCreateESignWorkflowSession({
    requirementId,
  });

  return useMutation({
    mutationFn: async (variables: SignableESignWorkflowSessionMutationVariables) => {
      const { compositeParentRequirementId } = variables;
      const existingSessions = await fetchESignWorkflowSessions({ requirementId, workerId });
      const [existingSession] = existingSessions.data;
      const pandaDocSessionId = existingSession?.attributes.esignProviderData?.sessionId;

      if (isDefined(existingSession) && isDefined(pandaDocSessionId)) {
        return existingSession;
      }

      if (!isDefined(existingSession)) {
        await createESignWorkflowSession({
          compositeParentRequirementId,
        });
      }

      return pollForPandaDocSessionId({ requirementId, workerId });
    },
    ...restOptions,
  });
}
/* eslint-enable unicorn/prevent-abbreviations */
